import React from 'react';

const PerformanceMetrics = ({ performanceData, loading, onClose }) => {
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <span className="mr-2 text-[#ff660e] text-xl">📊</span>
            Performance Metrics
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-300 p-1 rounded-full hover:bg-gray-700 transition-colors"
          >
            ✕
          </button>
        </div>
        <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-[#ff660e]/20 border-t-[#ff660e] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (!performanceData?.success) {
    return (
      <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <span className="mr-2 text-[#ff660e] text-xl">📊</span>
            Performance Metrics
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-300 p-1 rounded-full hover:bg-gray-700 transition-colors"
          >
            ✕
          </button>
        </div>
        <div className="text-center py-8 border border-gray-700 rounded-lg">
          <div className="text-gray-400 text-4xl mb-3">📊</div>
          <p className="text-gray-500">No performance data available</p>
        </div>
      </div>
    );
  }

  const { overview, engagements, time_series } = performanceData.performance || {};

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <span className="mr-2 text-[#ff660e] text-xl">📊</span>
          Performance Metrics
        </h3>
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-gray-300 p-1.5 rounded-full hover:bg-gray-700 transition-colors hover:scale-110"
        >
          ✕
        </button>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-4 hover:border-blue-500/50 transition-all duration-200">
            <div className="text-2xl font-bold text-blue-400 mb-1">
              {formatNumber(overview.total_views || 0)}
            </div>
            <div className="text-sm text-blue-300 flex items-center">
              <span className="mr-1">👁️</span>
              <span>Total Views</span>
            </div>
          </div>
          
          <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4 hover:border-green-500/50 transition-all duration-200">
            <div className="text-2xl font-bold text-green-400 mb-1">
              {formatNumber(overview.total_orders || 0)}
            </div>
            <div className="text-sm text-green-300 flex items-center">
              <span className="mr-1">🛒</span>
              <span>Total Orders</span>
            </div>
          </div>
          
          <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-4 hover:border-purple-500/50 transition-all duration-200">
            <div className="text-2xl font-bold text-purple-400 mb-1">
              {formatCurrency(overview.total_revenue || 0)}
            </div>
            <div className="text-sm text-purple-300 flex items-center">
              <span className="mr-1">💰</span>
              <span>Revenue</span>
            </div>
          </div>
          
          <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-xl p-4 hover:border-yellow-500/50 transition-all duration-200">
            <div className="text-2xl font-bold text-yellow-400 mb-1">
              {overview.conversion_rate?.toFixed(2) || 0}%
            </div>
            <div className="text-sm text-yellow-300 flex items-center">
              <span className="mr-1">📈</span>
              <span>Conversion Rate</span>
            </div>
          </div>
        </div>
      )}

      {/* Engagements */}
      {engagements && engagements.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-300 mb-4 flex items-center">
            <span className="mr-2">📈</span>
            Engagement Types
          </h4>
          <div className="space-y-2">
            {engagements.map((engagement, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between bg-gray-750 border border-gray-700 p-3 rounded-lg hover:bg-gray-700 hover:border-[#ff660e]/30 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff660e]/20 to-orange-400/20 flex items-center justify-center text-gray-300 group-hover:scale-110 transition-transform">
                    {engagement.engagement_type === 'click' ? '👆' :
                     engagement.engagement_type === 'save' ? '💾' :
                     engagement.engagement_type === 'share' ? '📤' :
                     '📊'}
                  </div>
                  <span className="text-gray-300 capitalize">{engagement.engagement_type}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-white font-semibold">{formatNumber(engagement.count)}</span>
                  <div className="text-sm text-gray-400 bg-gray-800 px-2 py-1 rounded border border-gray-600">
                    {formatNumber(engagement.unique_users)} users
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time Series */}
      {time_series && time_series.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-300 mb-4 flex items-center">
            <span className="mr-2">📅</span>
            Recent Activity
          </h4>
          <div className="space-y-2">
            {time_series.slice(0, 5).map((day, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between bg-gray-750 border border-gray-700 p-3 rounded-lg hover:bg-gray-700 hover:border-[#ff660e]/30 transition-all duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff660e]/20 to-orange-400/20 flex items-center justify-center text-gray-300">
                    {index === 0 ? '🔥' :
                     index === 1 ? '🚀' :
                     '📅'}
                  </div>
                  <span className="text-gray-300 font-medium">{day.date}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-400 bg-gray-800 px-2 py-1 rounded border border-gray-600 flex items-center">
                    <span className="mr-1">👁️</span>
                    {day.daily_views} views
                  </div>
                  <div className="text-sm text-gray-400 bg-gray-800 px-2 py-1 rounded border border-gray-600 flex items-center">
                    <span className="mr-1">📊</span>
                    {day.daily_engagements} engagements
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {overview && (
        <div className="mt-6 pt-4 border-t border-gray-700">
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-400 flex items-center">
              <span className="mr-2">📊</span>
              <span>Performance Summary</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-gray-300">
                <span className="text-green-400 font-semibold">{formatNumber(overview.total_orders || 0)}</span> orders
              </div>
              <div className="text-gray-300">
                <span className="text-purple-400 font-semibold">{formatCurrency(overview.total_revenue || 0)}</span> revenue
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMetrics;